import { useCallback } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type {
    DepositCurrency,
    DepositRefundRow,
    DepositRow,
    DepositStatusFilter,
} from '@/components/contracts/deposits/types';

/** Data layer for the Contract Deposits surface. */

export const DEPOSITS_PAGE_SIZE = 15;

interface UseContractDepositsListArgs {
    /** 1-based page number. */
    page: number;
    /** Already-debounced search term; '' means "no search". */
    search: string;
    status: DepositStatusFilter;
}

export interface ContractDepositsPage {
    rows: DepositRow[];
    /** Rides on every row; read off row 0 (0 when the page is empty). */
    totalCount: number;
}

/**
 * Paginated deposits list. Search spans deposit remarks, driver name/phone,
 * plate and transaction description — all of it server-side (the RPC exists
 * because that OR crosses four relations and PostgREST cannot express it).
 */
export function useContractDepositsList({ page, search, status }: UseContractDepositsListArgs) {
    return useQuery<ContractDepositsPage>({
        queryKey: ['contract-deposits', page, search, status],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_contract_deposits_paginated', {
                p_search: search || null,
                p_status: status === 'all' ? null : status,
                p_page: page,
                p_page_size: DEPOSITS_PAGE_SIZE,
            });
            if (error) throw error;
            const rows = (data || []) as DepositRow[];
            return { rows, totalCount: rows[0]?.total_count ?? 0 };
        },
        // Keeps the pager and table stable while the next page loads.
        placeholderData: keepPreviousData,
    });
}

/** Raw shape of the refunds select before the embedded transaction is flattened. */
interface RawDepositRefundRow {
    id: number;
    created_at: string;
    remarks: string | null;
    transaction_id: number;
    transactions:
        | { amount: number; currency: string; transaction_at: string; description: string | null }
        | { amount: number; currency: string; transaction_at: string; description: string | null }[]
        | null;
}

/** Refund history of one deposit, newest first. */
export function useDepositRefunds(depositId: number | null) {
    return useQuery<DepositRefundRow[]>({
        queryKey: ['contract-deposit-refunds', depositId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('contract_deposit_refunds')
                .select('id, created_at, remarks, transaction_id, transactions(amount, currency, transaction_at, description)')
                .eq('contract_deposit_id', depositId)
                .order('created_at', { ascending: false });
            if (error) throw error;

            return ((data || []) as RawDepositRefundRow[]).map((row) => {
                const tx = Array.isArray(row.transactions) ? row.transactions[0] : row.transactions;
                return {
                    id: row.id,
                    created_at: row.created_at,
                    remarks: row.remarks,
                    transaction_id: row.transaction_id,
                    amount: tx?.amount ?? 0,
                    currency: (tx?.currency ?? 'GHS') as DepositCurrency,
                    transaction_at: tx?.transaction_at ?? row.created_at,
                    description: tx?.description ?? null,
                };
            });
        },
        enabled: !!depositId,
    });
}

export interface DepositInvalidateOptions {
    /** Narrows the refunds key to one deposit; omit to drop every refund cache. */
    depositId?: number | null;
    /** Contract the deposit is linked to; drives the contract-insights key. */
    contractId?: number | null;
}

/**
 * The one invalidation helper every deposit mutation calls.
 *
 * Union of the plan's matrix (keys are prefix-matched by react-query):
 * - create deposit ......... contract-deposits, contract-deposits-analytics,
 *   contract-deposits-unassigned-pool, unassigned-deposits, unassigned-transactions,
 *   unassigned-transactions-count
 * - edit/delete deposit .... the above + contract-deposit-refunds, deposit-agreements, and —
 *   when assigned — ['contract-insights', String(contractId)], agreement-board-subjects,
 *   agreement-documents, agreement-document-arcs
 * - create/delete refund ... contract-deposits, ['contract-deposit-refunds', depositId],
 *   analytics, pool, unassigned-transactions(+count), and the same conditional contract keys
 *
 * `['contract-deposits']` IS A PREFIX, and that is load-bearing: the setup page's per-contract
 * read is `['contract-deposits', 'by-contract', contractId]`, so it is cleared by the first line
 * below without a line of its own. The retired `['deposit-payments']` key was that read's third
 * duplicate and has no invalidation here because it no longer exists.
 *
 * contract-insights is keyed with `String(contractId)` — the route param is a string,
 * so a numeric key would miss the mounted query — and only when contractId is non-null.
 * The three agreement keys are keyed with the NUMBER, which is how the board keys them.
 */
export function useDepositCacheInvalidator() {
    const queryClient = useQueryClient();

    return useCallback(
        (opts?: DepositInvalidateOptions) => {
            const { depositId, contractId } = opts ?? {};

            queryClient.invalidateQueries({ queryKey: ['contract-deposits'] });
            queryClient.invalidateQueries({ queryKey: ['contract-deposits-analytics'] });
            queryClient.invalidateQueries({ queryKey: ['contract-deposits-unassigned-pool'] });
            queryClient.invalidateQueries({ queryKey: ['unassigned-deposits'] });
            queryClient.invalidateQueries({ queryKey: ['unassigned-transactions'] });
            queryClient.invalidateQueries({ queryKey: ['unassigned-transactions-count'] });
            queryClient.invalidateQueries({ queryKey: ['deposit-agreements'] });
            queryClient.invalidateQueries({
                queryKey: depositId != null ? ['contract-deposit-refunds', depositId] : ['contract-deposit-refunds'],
            });
            // ATTACHING A DEPOSIT TO A CONTRACT CHANGES ITS AGREEMENT BLOCK. The depositor name
            // stops being the only identity, so the block has to re-derive rather than keep
            // showing the rules that applied while the deposit belonged to nobody.
            queryClient.invalidateQueries({
                queryKey: depositId != null ? ['deposit-agreement', depositId] : ['deposit-agreement'],
            });

            if (contractId != null) {
                queryClient.invalidateQueries({ queryKey: ['contract-insights', String(contractId)] });
                // A DEPOSIT IS A SIGNING SUBJECT, so linking one to a contract adds a row to
                // that contract's signing board. The board derives its slots from these three
                // reads and none of them was invalidated here — which is why a deposit linked
                // from setup showed no deposit agreement until the page was hard-refreshed.
                queryClient.invalidateQueries({ queryKey: ['agreement-board-subjects', contractId] });
                queryClient.invalidateQueries({ queryKey: ['agreement-documents', contractId] });
                queryClient.invalidateQueries({ queryKey: ['agreement-document-arcs', contractId] });
                // A DEPOSIT IS STILL A READINESS INPUT, THOUGH NO LONGER A GATE ON ITS OWN. The
                // absence of one stopped being a refusal at [D-B41], but every deposit that exists
                // has to be papered [D-B15] — so linking or unlinking one moves the deposits
                // section's budget and its `AGREEMENT_DEPOSIT_*` arms, and the gate has to re-read
                // rather than keep the pre-write answer.
                queryClient.invalidateQueries({ queryKey: ['contract-setup-status', contractId] });
            }
        },
        [queryClient],
    );
}
